'use client'

import { motion, AnimatePresence } from 'framer-motion'
import { User, Mail, Phone, Building2, Target, Globe, Palette, FileText, Clock, Layers } from 'lucide-react'
import type { FormData } from '@/hooks/useAppStore'
import { getLabel } from '@/tipos/lead'

interface ProjectSummaryProps {
  formData: FormData
  currentStep: string
}

export function ProjectSummary({ formData }: ProjectSummaryProps) {
  // Construir lista de items preenchidos
  const items: { icon: typeof User; label: string; value: string }[] = []

  if (formData.nome) {
    items.push({ icon: User, label: 'Nome', value: formData.nome })
  }

  if (formData.email) {
    items.push({ icon: Mail, label: 'E-mail', value: formData.email })
  }

  if (formData.whatsapp) {
    items.push({ icon: Phone, label: 'WhatsApp', value: formData.whatsapp })
  }

  if (formData.empresa) {
    items.push({ icon: Building2, label: 'Empresa', value: formData.empresa })
  }

  if (formData.demandType) {
    items.push({ icon: User, label: 'Perfil', value: getLabel('demandType', formData.demandType) })
  }

  if (formData.situation) {
    items.push({ icon: Globe, label: 'Situação', value: getLabel('situation', formData.situation) })
  }

  if (formData.objective) {
    items.push({ icon: Target, label: 'Objetivo', value: getLabel('objective', formData.objective) })
  }

  if (formData.features.length > 0) {
    const featuresText = formData.features.length > 2
      ? `${formData.features.length} funcionalidades`
      : formData.features.join(', ')
    items.push({ icon: Layers, label: 'Features', value: featuresText })
  }

  if (formData.hasLogo !== null) {
    items.push({ icon: Palette, label: 'Logo', value: formData.hasLogo ? 'Tem logo' : 'Precisa criar' })
  }

  if (formData.contentStatus) {
    items.push({ icon: FileText, label: 'Conteúdo', value: getLabel('contentStatus', formData.contentStatus) })
  }

  if (formData.urgency) {
    items.push({ icon: Clock, label: 'Prazo', value: getLabel('urgency', formData.urgency) })
  }

  if (formData.projectType) {
    items.push({ icon: Layers, label: 'Plano', value: getLabel('projectType', formData.projectType) })
  }

  if (formData.additional) {
    const truncated = formData.additional.length > 50
      ? formData.additional.substring(0, 50) + '...'
      : formData.additional
    items.push({ icon: FileText, label: 'Detalhes', value: truncated })
  }

  if (items.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-indigo-950/5 dark:bg-white/5 flex items-center justify-center">
            <FileText size={24} className="text-indigo-950/30 dark:text-white/30" />
          </div>
          <p className="text-indigo-950/40 dark:text-white/40 text-sm">
            As informações do projeto aparecerão aqui
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto p-4">
      <AnimatePresence mode="popLayout">
        {items.map((item, index) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ delay: index * 0.05 }}
            className="flex items-start gap-3 py-3 border-b border-purple-500/20 dark:border-white/10 last:border-0"
          >
            <div className="w-8 h-8 rounded-lg bg-indigo-950/5 dark:bg-white/5 flex items-center justify-center flex-shrink-0">
              <item.icon size={14} className="text-indigo-950/50 dark:text-white/50" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-indigo-950/40 dark:text-white/40 mb-0.5">{item.label}</p>
              <p className="text-sm text-indigo-950/80 dark:text-white/80 truncate">{item.value}</p>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  )
}
